import {
	Box,
	Button,
	CardActions,
	CardContent,
	Divider,
	FormHelperText,
	Grid,
	IconButton,
	InputAdornment,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useController,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppRichTextEditor from '@/common/components/editor/AppRichTextEditor';

import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import {
	DialogAssignPage,
	DialogClickToType,
	DialogContentType,
	DialogShowPage,
	Platform,
	ShowTimeType,
} from '@/api/enum/common.enum';
import { PopupMessageDTO } from '@/api/DTO/app-setting/dialog.interface';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_TIME_FORMAT, isTime } from '@/utils/dateHelper';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { FileInfo } from '@/api/DTO/common.interface';
import { useFileUploader } from '@/hooks/useFileUploader';
import {
	useCreateDialogMutation,
	useUpdateDialogMutation,
} from '@/api/app-setting/dialogApiSlice';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppDateTimePicker from '@/common/components/form-inputs/AppDateTimePicker';
import AppCheckBoxGroup from '@/common/components/form-inputs/AppCheckBoxGroup';
import { DIALOG_LIST_PAGE } from '@/common/constants/common';

interface Props {
	type: FormType;
	details?: PopupMessageDTO;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function InformationInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [createDialog] = useCreateDialogMutation();
	const [updateDialog] = useUpdateDialogMutation();
	const [imageFile, setImageFile] = useState<FileInfo | null>(null);
	const [videoFile, setVideoFile] = useState<FileInfo | null>(null);
	const [coverFile, setCoverFile] = useState<FileInfo | null>(null);
	const imageInputRef = useRef<HTMLInputElement>(null);
	const imageInputRef2 = useRef<HTMLInputElement>(null);
	const imageInputRef3 = useRef<HTMLInputElement>(null);
	const { uploadFile } = useFileUploader();

	const dialogShowPageList = [
		{ id: DialogShowPage.WelcomePage, name: t('dialogList.welcome') },
		{ id: DialogShowPage.Index, name: t('dialogList.index') },
		{ id: DialogShowPage.Exercise, name: t('dialogList.exercise') },
		{ id: DialogShowPage.Collect, name: t('dialogList.collect') },
		{ id: DialogShowPage.MyData, name: t('dialogList.myData') },
		{ id: DialogShowPage.Task, name: t('dialogList.task') },
		{ id: DialogShowPage.Achievement, name: t('dialogList.achievement') },
	];

	const dialogContentTypeList = [
		{ id: DialogContentType.Image, name: t('dialogList.image') },
		{ id: DialogContentType.ImageText, name: t('dialogList.imageText') },
		{ id: DialogContentType.Video, name: t('dialogList.video') },
	];

	const showTimeTypeList = [
		{ id: ShowTimeType.Every, name: t('dialogList.every') },
		{ id: ShowTimeType.Once, name: t('dialogList.once') },
		{ id: ShowTimeType.One, name: t('dialogList.one') },
	];

	const dialogClickToTypeList = [
		{ id: DialogClickToType.Null, name: t('dialogList.null') },
		{ id: DialogClickToType.OutSide, name: t('dialogList.outSide') },
		{ id: DialogClickToType.InSide, name: t('dialogList.inSide') },
	];

	const platformList = [
		{ value: Platform.IOS, label: Platform.IOS },
		{ value: Platform.Android, label: Platform.Android },
	];

	const defaultValues: DefaultValues<PopupMessageDTO> = {
		type: null,
		mediaType: null,
		linkType: null,
	};

	useEffect(() => {
		if (details) {
			details?.titleZh && methods.setValue('titleZh', details?.titleZh);
			details?.titleEn && methods.setValue('titleEn', details?.titleEn);
			if (Number(details?.type) > -1) methods.setValue('type', details?.type);

			const platformArr = [];
			details?.publishedAndroid && platformArr.push(Platform.Android);
			details?.publishedIos && platformArr.push(Platform.IOS);
			methods.setValue('platform', platformArr);

			details?.publishedStartAt &&
				methods.setValue('publishedStartAt', details?.publishedStartAt);
			details?.publishedEndAt &&
				methods.setValue('publishedEndAt', details?.publishedEndAt);

			if (Number(details?.mediaType) > -1)
				methods.setValue('mediaType', details?.mediaType);

			if (details?.image) {
				setImageFile(details?.image);
				methods.setValue('imageFile', details?.image.name);
			}

			details?.descriptionZh &&
				methods.setValue('descriptionZh', details?.descriptionZh);
			details?.descriptionEn &&
				methods.setValue('descriptionEn', details?.descriptionEn);
			if (details?.video) {
				setVideoFile(details?.video);
				methods.setValue('videoFormFile', details?.video.name);
			}
			if (details?.videoCover) {
				setCoverFile(details?.videoCover);
				methods.setValue('coverFile', details?.videoCover.name);
			}
			if (Number(details?.linkType) > -1)
				methods.setValue('linkType', details?.linkType);

			details?.linkText && methods.setValue('linkText', details?.linkText);
			details?.linkUrl && methods.setValue('linkUrl', details?.linkUrl);
			if (Number(details?.linkPage) > -1)
				methods.setValue('linkPage', details?.linkPage);

			details?.showPageList &&
				methods.setValue('showPageList', details?.showPageList);
		}
	}, [details]);

	const formSchema = Yup.object().shape({
		publishedStartAt: Yup.string()
			.nullable()
			.test(
				'is-valid-iso-date',
				t('errorMessage.pleaseSelectTime'),
				(value) => {
					return isTime(value);
				}
			),
		publishedEndAt: Yup.string()
			.nullable()
			.test(
				'is-valid-iso-date',
				t('errorMessage.pleaseSelectTime'),
				(value) => {
					return isTime(value);
				}
			),
		titleZh: Yup.string().required(t('errorMessage.required')),
		type: Yup.string().required(t('errorMessage.pleaseSelect')),
		mediaType: Yup.string().required(t('errorMessage.pleaseSelect')),
		platform: Yup.array()
			.min(1, t('errorMessage.pleaseSelect'))
			.required(t('errorMessage.pleaseSelect')),
		showPageList: Yup.array()
			.required(t('errorMessage.pleaseSelect'))
			.min(1, t('errorMessage.pleaseSelect')),
		imageFile: Yup.string()
			.nullable()
			.when('mediaType', {
				is: (mediaType: string | number) =>
					mediaType == DialogContentType.Image,
				then: (schema) => schema.required(t('errorMessage.pleaseUpload')),
			}),
		coverFile: Yup.string()
			.nullable()
			.when('mediaType', {
				is: (mediaType: string | number) =>
					mediaType == DialogContentType.Video,
				then: (schema) => schema.required(t('errorMessage.pleaseUpload')),
			}),
		videoFormFile: Yup.string()
			.nullable()
			.when('mediaType', {
				is: (mediaType: string | number) =>
					mediaType == DialogContentType.Video,
				then: (schema) => schema.required(t('errorMessage.pleaseUpload')),
			}),

		linkType: Yup.string().required(t('errorMessage.pleaseSelect')),
		linkUrl: Yup.string()
			.nullable()
			.when('linkType', {
				is: (linkType: string | number) =>
					linkType == DialogClickToType.OutSide,
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
		linkText: Yup.string()
			.nullable()
			.when('linkType', {
				is: (linkType: string | number) =>
					linkType == DialogClickToType.OutSide ||
					linkType == DialogClickToType.InSide,
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
		linkPage: Yup.string()
			.nullable()
			.when('linkType', {
				is: (linkType: string | number) => linkType == DialogClickToType.InSide,
				then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			}),
	});
	const methods = useForm<PopupMessageDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange', // 在字段变化时触发验证
		reValidateMode: 'onChange', // 重新验证模式
	});

	const imageField = useController({
		name: 'imageFile',
		control: methods.control,
	});
	const coverField = useController({
		name: 'coverFile',
		control: methods.control,
	});
	const watchMediaType = methods.watch('mediaType');
	const watchLinkType = methods.watch('linkType');

	useEffect(() => {
		const subscription = methods.watch((value, field) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const handleDeleteFile = (type: string) => {
		if (type == 'image') {
			setImageFile(null);
			methods.setValue('imageFile', '');
			imageInputRef.current && (imageInputRef.current.value = '');
		} else if (type == 'video') {
			setVideoFile(null);
			methods.setValue('videoFormFile', '');
			imageInputRef2.current && (imageInputRef2.current.value = '');
		} else if (type == 'cover') {
			setCoverFile(null);
			methods.setValue('coverFile', '');
			imageInputRef3.current && (imageInputRef3.current.value = '');
		}
	};

	const handleUpload = async (
		event: React.ChangeEvent<HTMLInputElement>,
		type: string
	) => {
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		const res = await uploadFile(selectedFile);
		if (res) {
			if (type == 'image') {
				setImageFile(res);
				methods.setValue('imageFile', res.name);
				methods.trigger('imageFile');
			} else if (type == 'video') {
				setVideoFile(res);
				methods.setValue('videoFormFile', res.name);
				methods.trigger('videoFormFile');
			} else if (type == 'cover') {
				setCoverFile(res);
				methods.setValue('coverFile', res.name);
				methods.trigger('coverFile');
			}
		}
	};

	const onSubmit: SubmitHandler<PopupMessageDTO> = async (data) => {
		const obj: PopupMessageDTO = {
			titleZh: data.titleZh,
			titleEn: data.titleEn,
			showPageList: data.showPageList,
			type: data.type,
			publishedAndroid: data.platform?.find(
				(item: any) => item == Platform.Android
			)
				? 1
				: 0,
			publishedIos: data.platform?.find((item: any) => item == Platform.IOS)
				? 1
				: 0,
			publishedStartAt: data.publishedStartAt,
			publishedEndAt: data.publishedEndAt,
			mediaType: data.mediaType,
			linkType: data.linkType,
		};
		if (data.mediaType == DialogContentType.Image) {
			obj.image = imageFile;
		}
		if (data.mediaType == DialogContentType.ImageText) {
			obj.descriptionZh = data.descriptionZh;
			obj.descriptionEn = data.descriptionEn;
		}
		if (data.mediaType == DialogContentType.Video) {
			obj.video = videoFile;
			obj.videoCover = coverFile;
		}
		if (data.linkType == DialogClickToType.OutSide) {
			obj.linkUrl = data.linkUrl;
			obj.linkText = data.linkText;
		}
		if (data.linkType == DialogClickToType.InSide) {
			obj.linkPage = data.linkPage;
			obj.linkText = data.linkText;
		}
		const response =
			type == FormType.create
				? await createDialog(obj)
				: await updateDialog({
						...obj,
						id: details?.id || '',
				  });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createSuccess')
					: t('snackbar.changeContentSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate('/app-setting/dialog-list');
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate('/app-setting/dialog-list');
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};
	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('information.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('information.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<Grid item xs={6}>
									<AppTextField
										control={methods.control}
										name='titleZh'
										label={t('dialogList.dialogTitle')}
									/>
								</Grid>
							)}
							{currentTab == 'en' && (
								<Grid item xs={6}>
									<AppTextField
										control={methods.control}
										name='titleEn'
										label={t('dialogList.dialogTitle_')}
									/>
								</Grid>
							)}
							<Grid item xs={6}>
								<AppMultipleDropDownMenu
									control={methods.control}
									name='showPageList'
									label={t('dialogList.showPage')}
									options={dialogShowPageList}
								/>
							</Grid>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('dialogList.showTime')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='type'
									row
									options={showTimeTypeList}
								/>
							</Grid>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('dialogList.platform')}
								</Typography>
								<AppCheckBoxGroup name='platform' options={platformList} row />
							</Grid>
							<Grid item xs={6}>
								<AppDateTimePicker
									minimumDate={dayjs()
										.add(0, 'day')
										.format(API_DATE_TIME_FORMAT)}
									control={methods.control}
									name='publishedStartAt'
									label={t('taskManage.pushedTime_')}
									formatDate={API_DATE_TIME_FORMAT}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppDateTimePicker
									minimumDate={dayjs()
										.add(0, 'day')
										.format(API_DATE_TIME_FORMAT)}
									control={methods.control}
									name='publishedEndAt'
									label={t('taskManage.pulledTime_')}
									formatDate={API_DATE_TIME_FORMAT}
								/>
							</Grid>
						</Grid>
						<Divider />
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('dialogList.contentType')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='mediaType'
									row
									options={dialogContentTypeList}
								/>
							</Grid>
							{watchMediaType == DialogContentType.Image && (
								<Grid item xs={6}>
									<Typography variant='body2' color='common.neutral40'>
										{t('dialogList.dialogImage')}
									</Typography>
									{imageFile && (
										<img
											src={imageFile?.url || ''}
											width={390}
											height={138}
											style={{
												objectFit: 'contain',
											}}
										/>
									)}

									<Box display={'flex'} alignItems={'center'}>
										{imageFile ? (
											<Button
												component='label'
												sx={{ height: 24 }}
												endIcon={<LoopOutlinedIcon />}
											>
												{t('information.changeImage')}
												<VisuallyHiddenInput
													type='file'
													name='thumbnail'
													accept='image/*'
													ref={imageInputRef}
													onChange={(event) => {
														handleUpload(event, 'image');
													}}
												/>
											</Button>
										) : (
											<Button
												component='label'
												sx={{ height: 24 }}
												endIcon={<DriveFolderUploadOutlinedIcon />}
											>
												{t('information.uploadImage')}
												<VisuallyHiddenInput
													type='file'
													name='thumbnail'
													accept='image/*'
													ref={imageInputRef}
													onChange={(event) => {
														handleUpload(event, 'image');
													}}
												/>
											</Button>
										)}
										{imageFile && (
											<Button
												component='label'
												sx={{ height: 24, marginLeft: '24px' }}
												color='error'
												endIcon={<DeleteOutlinedIcon />}
												onClick={() => {
													handleDeleteFile('image');
												}}
											>
												{t('information.deleteImage')}
											</Button>
										)}
									</Box>
									<FormHelperText color='error' style={{ color: '#d32f2f' }}>
										{imageField.fieldState.error?.message}
									</FormHelperText>
								</Grid>
							)}
							{watchMediaType == DialogContentType.ImageText &&
								currentTab == 'zh' && (
									<Grid item xs={12}>
										<Typography variant='body2' color='common.neutral40'>
											{t('dialogList.description_')}
										</Typography>
										<AppRichTextEditor
											control={methods.control}
											name='descriptionZh'
										/>
									</Grid>
								)}
							{watchMediaType == DialogContentType.ImageText &&
								currentTab == 'en' && (
									<Grid item xs={12}>
										<Typography variant='body2' color='common.neutral40'>
											{t('dialogList.description_')}
										</Typography>
										<AppRichTextEditor
											control={methods.control}
											name='descriptionEn'
										/>
									</Grid>
								)}
							{watchMediaType == DialogContentType.Video && (
								<>
									<Grid item xs={6}>
										<AppTextField
											disabled
											control={methods.control}
											name='videoFormFile'
											label={t('dialogList.dialogVideo')}
											InputProps={{
												endAdornment: (
													<InputAdornment position='end'>
														{videoFile?.url && (
															<IconButton
																type='button'
																onClick={() => handleDeleteFile('video')}
															>
																<CloseIcon />
															</IconButton>
														)}

														<IconButton component='label'>
															<AttachFileIcon />
															<VisuallyHiddenInput
																type='file'
																accept='.mp4'
																onChange={(event) =>
																	handleUpload(event, 'video')
																}
																ref={imageInputRef2}
															/>
														</IconButton>
													</InputAdornment>
												),
											}}
										/>
									</Grid>
									<Grid item xs={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('dialogList.coverImage')}
										</Typography>
										{coverFile && (
											<img
												src={coverFile?.url || ''}
												width={390}
												height={138}
												style={{
													objectFit: 'contain',
												}}
											/>
										)}

										<Box display={'flex'} alignItems={'center'}>
											{coverFile ? (
												<Button
													component='label'
													sx={{ height: 24 }}
													endIcon={<LoopOutlinedIcon />}
												>
													{t('information.changeImage')}
													<VisuallyHiddenInput
														type='file'
														name='thumbnail'
														accept='image/*'
														ref={imageInputRef3}
														onChange={(event) => {
															handleUpload(event, 'cover');
														}}
													/>
												</Button>
											) : (
												<Button
													component='label'
													sx={{ height: 24 }}
													endIcon={<DriveFolderUploadOutlinedIcon />}
												>
													{t('information.uploadImage')}
													<VisuallyHiddenInput
														type='file'
														name='thumbnail'
														accept='image/*'
														ref={imageInputRef3}
														onChange={(event) => {
															handleUpload(event, 'cover');
														}}
													/>
												</Button>
											)}
											{coverFile && (
												<Button
													component='label'
													sx={{ height: 24, marginLeft: '24px' }}
													color='error'
													endIcon={<DeleteOutlinedIcon />}
													onClick={() => {
														handleDeleteFile('cover');
													}}
												>
													{t('information.deleteImage')}
												</Button>
											)}
										</Box>
										<FormHelperText color='error' style={{ color: '#d32f2f' }}>
											{coverField.fieldState.error?.message}
										</FormHelperText>
									</Grid>
								</>
							)}
						</Grid>
						<Divider />
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('dialogList.clickTo')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='linkType'
									row
									options={dialogClickToTypeList}
								/>
							</Grid>
							{watchLinkType == DialogClickToType.OutSide && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='linkUrl'
											label={t('dialogList.dialogLink')}
										/>
									</Grid>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='linkText'
											label={t('dialogList.dialogLinkButtonText')}
										/>
									</Grid>
								</>
							)}
							{watchLinkType == DialogClickToType.InSide && (
								<>
									<Grid item xs={6}>
										<AppDropDownMenu
											control={methods.control}
											name='linkPage'
											label={t('dialogList.dialogToPage')}
											options={DIALOG_LIST_PAGE}
										/>
									</Grid>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='linkText'
											label={t('dialogList.dialogLinkButtonText')}
										/>
									</Grid>
								</>
							)}
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
